import { useEffect, useRef, useState } from 'react';
import { buroApi } from './api';

// Respuestas ya pedidas en esta sesión (el catálogo no cambia entre consultas).
const CACHE = new Map();
const ESPERA_MS = 400;

/*
 * Consulta el catálogo de C.P. cuando el texto llega a 5 dígitos (con una
 * pequeña espera para no pedir en cada tecla). Devuelve
 * { estado: 'idle'|'cargando'|'ok'|'no_encontrado'|'error', datos }.
 * Si el servicio falla no estorba: el estado es 'error' y los campos quedan libres.
 */
export const useCodigoPostal = (cp) => {
  const valido = /^\d{5}$/.test(String(cp || ''));
  const [res, setRes] = useState({ cp: null, estado: 'idle', datos: null });

  useEffect(() => {
    if (!valido) return undefined;
    if (CACHE.has(cp)) {
      const d = CACHE.get(cp);
      setRes({ cp, estado: d.encontrado ? 'ok' : 'no_encontrado', datos: d });
      return undefined;
    }
    const ctrl = new AbortController();
    setRes({ cp, estado: 'cargando', datos: null });
    const t = setTimeout(() => {
      buroApi.getCodigoPostal(cp, ctrl.signal)
        .then((d) => {
          if (ctrl.signal.aborted) return;
          CACHE.set(cp, d);
          setRes({ cp, estado: d.encontrado ? 'ok' : 'no_encontrado', datos: d });
        })
        .catch((e) => {
          if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
          setRes({ cp, estado: 'error', datos: null });
        });
    }, ESPERA_MS);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [cp, valido]);

  // Un resultado de otro C.P. (o con el C.P. incompleto) no vale.
  if (!valido || res.cp !== cp) return { estado: 'idle', datos: null };
  return res;
};

export default useCodigoPostal;

/*
 * Autollenado del domicilio de PM/PFAE (EM, AC, AV) al capturar el C.P.
 * `cp` es el valor actual, `claves` { colonia, municipio, ciudad, estado } (clavesDomicilio) y
 * `setValores` el setter del objeto de valores. Cuando el C.P. está completo y
 * encontrado, una sola vez por C.P. (lo que el usuario edite después no se pisa):
 *  - C.P. que el usuario cambió: estado, municipio y ciudad se sobrescriben; la
 *    colonia se queda si es del C.P., si no se elige la única o se vacía.
 *  - C.P. que ya traía el registro: solo se llenan los vacíos.
 * Devuelve el resultado de useCodigoPostal (para la lista de colonias y el aviso).
 */
export const useAutollenadoDomicilio = (cp, claves, setValores) => {
  const res = useCodigoPostal(cp);
  const inicial = useRef(cp);
  const llenado = useRef(null);
  useEffect(() => {
    if (res.estado !== 'ok' || llenado.current === cp || !claves) return;
    llenado.current = cp;
    const x = res.datos;
    const pisar = cp !== inicial.current;
    const cols = x.colonias || [];
    setValores((v) => {
      const dato = (k, nuevo) => (nuevo && (pisar || !v[k]) ? nuevo : (v[k] ?? ''));
      const unica = cols.length === 1 ? cols[0] : '';
      const actual = v[claves.colonia] ?? '';
      let colonia = actual;
      if (!actual) colonia = unica;
      else if (pisar && cols.length && !cols.includes(actual)) colonia = unica;
      return {
        ...v,
        [claves.estado]: dato(claves.estado, x.estado),
        [claves.municipio]: dato(claves.municipio, x.municipio),
        [claves.ciudad]: dato(claves.ciudad, x.ciudad),
        [claves.colonia]: colonia,
      };
    });
  }, [res, cp, claves, setValores]);
  return res;
};
